// The two shells in one component. On a phone the screen is the app: the
// children fill it. On a desk (≥ --desk-min) the app is a workbench: the
// phone's home list stands as a rail on the left, scrolling on its own, and
// the children take the stage beside it. Only the shell that is showing is
// rendered, so a phone never pays for the rail's DOM.
//
// The Workbench owns the `.app.paper` root in both shells. Children are the
// screen's content and must not bring a `main` or `.app` of their own: on a
// desk `.app` is the contained window, and a second one would nest a window
// inside the stage.
import { useSyncExternalStore, type ReactNode } from 'react';
import { deskMin, isDesk } from '@app/ui';
import { HomeList } from '../routes/home/HomeList';
import './workbench.css';

export interface WorkbenchProps {
  /** The project open on the stage, highlighted in the rail. */
  selectedId?: string;
  children: ReactNode;
}

function subscribe(onChange: () => void): () => void {
  const mq = matchMedia(`(min-width: ${deskMin()}px)`);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/** True on a desk, and again whenever the window crosses the breakpoint. */
export function useDesk(): boolean {
  return useSyncExternalStore(subscribe, isDesk, () => false);
}

export function Workbench({ selectedId, children }: WorkbenchProps) {
  const desk = useDesk();
  if (!desk) return <main className="app paper">{children}</main>;
  return (
    <div className="app paper workbench">
      <aside className="rail" aria-label="Your projects">
        <HomeList selectedId={selectedId} />
      </aside>
      <main className="stage">{children}</main>
    </div>
  );
}
